import React, { FC, useCallback } from 'react'
import { IconButton } from 'transfix-ui/components/IconButton'
import { OverflowMenu, useOverflowToggle } from 'transfix-ui/components/OverflowMenu'
import { useGlobalToast } from 'transfix-ui/components/Toast'
import Box from 'transfix-ui/core/Box'
import { useFMSAnalytics } from 'apps/fleet-planner/hooks'
import { useSavedLaneMutations } from 'apps/fleet-planner/hooks/useSavedLaneMutations'
import { BookableShipmentSavedSearch } from 'apps/fleet-planner/types'
import { IDropdownOption } from 'components/forms'
import { EllipsisIcon, EditIcon, DeleteIcon, BellOffIcon, BellOnIcon } from 'transfix-icons'

const FAILURE_MESSAGE = 'Server error. Contact tech support for assistance.'
const NOTIFICATIONS_ON_MESSAGE = 'You will start receiving emails about this lane'
const NOTIFICATIONS_OFF_MESSAGE = 'You will no longer recieve emails about this lane'

export interface ISaveLaneRowMenuProps {
  savedLaneId: number
  notifications: boolean | undefined | null
  onDelete: () => void
  onEdit: () => void
  onNotificationToggleSuccess: (updatedSavedLane: BookableShipmentSavedSearch) => void
}
export const SavedLaneRowMenu: FC<ISaveLaneRowMenuProps> = ({
  notifications,
  onDelete,
  onEdit,
  onNotificationToggleSuccess,
  savedLaneId,
}) => {
  const { trackEvent } = useFMSAnalytics()
  const { openToast } = useGlobalToast()
  const { clickOverflowTrigger, expanded, setExpanded } = useOverflowToggle()

  const { updateSavedLaneNotificationSetting } = useSavedLaneMutations({ savedLaneId })

  const showErrorToast = useCallback(() => {
    openToast({ toastMessage: FAILURE_MESSAGE, toastType: 'alert' })
  }, [openToast])

  const showSuccessToast = useCallback(() => {
    openToast({
      toastType: 'default',
      toastMessage: notifications ? NOTIFICATIONS_OFF_MESSAGE : NOTIFICATIONS_ON_MESSAGE,
    })
  }, [notifications, openToast])

  const trackToggleNotificationEvent = useCallback(
    (updatedNotificationSetting: boolean) => {
      trackEvent(
        'Saved Lanes',
        updatedNotificationSetting
          ? 'TURNED_OFF_SAVED_LANE_NOTIFICATIONS'
          : 'TURNED_ON_SAVED_LANE_NOTIFICATIONS'
      )
    },
    [trackEvent]
  )

  const notificationToggleHandler = useCallback(async () => {
    try {
      const response = await updateSavedLaneNotificationSetting(!notifications)
      if (response) {
        trackToggleNotificationEvent(!notifications)
        showSuccessToast()
        onNotificationToggleSuccess(response)
      } else {
        showErrorToast()
      }
    } catch {
      showErrorToast()
    }
  }, [
    notifications,
    onNotificationToggleSuccess,
    showErrorToast,
    showSuccessToast,
    trackToggleNotificationEvent,
    updateSavedLaneNotificationSetting,
  ])

  const editOption = {
    label: 'Edit',
    itemComponent: (
      <Box display='flex' alignItems='center'>
        <EditIcon size='large' />
        <span className='ml2'>Edit</span>
      </Box>
    ),
    action: onEdit,
  }
  const deleteOption = {
    label: 'Delete',
    itemComponent: (
      <Box display='flex' alignItems='center'>
        <DeleteIcon size='large' />
        <span className='ml2'>Delete</span>
      </Box>
    ),
    action: onDelete,
  }
  const notificationsOption = {
    label: 'Notification',
    itemComponent: (
      <Box display='flex' alignItems='center'>
        {notifications ? <BellOnIcon size='medium' /> : <BellOffIcon size='medium' />}
        <span className='ml2'>Turn Notifications {notifications ? <b>Off</b> : <b>On</b>}</span>
      </Box>
    ),
    action: notificationToggleHandler,
  }
  const options: IDropdownOption[] = [editOption, deleteOption, notificationsOption]
  return (
    <OverflowMenu
      options={options}
      trigger={
        <IconButton
          color='tertiary'
          label='overflow-trigger'
          Icon={EllipsisIcon}
          onClick={clickOverflowTrigger}
          data-test='saved-lane-row-menu-button'
          size='large'
        />
      }
      expanded={expanded}
      onExpanded={setExpanded}
      dropdownId='saved-lane-row-actions'
      dataTest='saved-lane'
    />
  )
}

export default SavedLaneRowMenu
